import {AssetContainer} from '@babylonjs/core/assetContainer.js';
import {LoadAssetContainerAsync} from '@babylonjs/core/Loading/sceneLoader.js';
import {Scene} from '@babylonjs/core/scene.js';
import {Vector3} from '@babylonjs/core/Maths/math.vector.js';
import {AbstractMesh} from '@babylonjs/core/Meshes/abstractMesh.js';
import {TransformNode} from '@babylonjs/core/Meshes/transformNode.js';
import {PBRMaterial} from '@babylonjs/core/Materials/PBR/pbrMaterial.js';
import '@babylonjs/loaders/glTF';
export const assetNames=['elmir_house_residential_01','elmir_house_residential_01_lod1','elmir_central_monument_01','elmir_tree','elmir_road_10m','elmir_grass_5m','elmir_temple_01','elmir_temple_01_lod1','elmir_shop_01','elmir_shop_01_lod1','elmir_blacksmith_01','elmir_blacksmith_01_lod1','elmir_forge_furnace_01','elmir_forge_anvil_01','elmir_shop_decor_01'] as const;
export type AssetName=typeof assetNames[number];
export class Assets {
 private containers=new Map<AssetName,AssetContainer>();
 status='not-started';requests=0;loadMs=0;private sharedTextures=new Map<string,any>();private sharedMaterials=new Map<string,any>();
 private dedupe(c:AssetContainer){
  const duplicates=new Set<any>();
  for(const material of c.materials){const m=material as any;for(const slot of ['albedoTexture','bumpTexture','metallicTexture','ambientTexture']){
   const t=m[slot];if(!t)continue;const key=[slot,t.gammaSpace,t.coordinatesIndex,t.wrapU,t.wrapV,t.uScale,t.vScale].join(':');
   const shared=this.sharedTextures.get(key);if(shared&&shared!==t){m[slot]=shared;duplicates.add(t);}else this.sharedTextures.set(key,t);
  }}
  c.textures=c.textures.filter(t=>!duplicates.has(t));for(const t of duplicates)t.dispose();
  const replaced=new Set<any>();
  for(const material of c.materials){const m=material as PBRMaterial;const key=JSON.stringify([m.name,m.albedoColor?.asArray(),m.metallic,m.roughness,m.backFaceCulling,m.alpha,m.bumpTexture?.level]);const cached=this.sharedMaterials.get(key);if(cached){for(const mesh of c.meshes)if(mesh.material===m)mesh.material=cached;replaced.add(m);}else this.sharedMaterials.set(key,m);}
  c.materials=c.materials.filter(m=>!replaced.has(m));for(const m of replaced)m.dispose(false,false);
 }

 constructor(private scene:Scene){}
 async load(progress:(text:string)=>void){
  const started=performance.now();
  for(const name of assetNames){
   progress('Загрузка: '+name);
   this.status='loading '+name;this.requests++;
   const c=await LoadAssetContainerAsync(import.meta.env.BASE_URL+'models/'+name+'.glb',this.scene).catch(error=>{this.status='error '+name;throw new Error('Ошибка загрузки GLB '+name+': '+String(error));});
   const geometry=c.meshes.filter(m=>m.getTotalVertices()>0);
   if(!geometry.length||!c.materials.length)throw new Error(name+': нет геометрии или материалов');
   for(const m of geometry){
    const p=m.getVerticesData('position');
    if(!p||!Array.from(p).every(Number.isFinite))throw new Error(name+': некорректная геометрия');
   }
   // Source box faces have inward winding (the GLB validator permits this).
   // Keep original bytes; render both sides and correct back-face lighting.
   for(const material of c.materials)if(['elmir_tree','elmir_road_10m','elmir_grass_5m'].includes(name)){
    material.backFaceCulling=false;
    if(material instanceof PBRMaterial)material.twoSidedLighting=true;
   }
   if(["elmir_temple_01", "elmir_temple_01_lod1", "elmir_shop_01", "elmir_shop_01_lod1", "elmir_blacksmith_01", "elmir_blacksmith_01_lod1", "elmir_forge_furnace_01", "elmir_forge_anvil_01", "elmir_shop_decor_01"].includes(name))this.dedupe(c);
   this.containers.set(name,c);this.status='ready';this.loadMs=performance.now()-started;
  }
 }
 private surfaceMaterials=new Map<AssetName,import('@babylonjs/core/Materials/material.js').Material>();
 surfaceMaterial(name:AssetName){
 if(!this.surfaceMaterials.has(name)){const m=this.material(name).clone('terrain-'+name)!;m.backFaceCulling=true;m.sideOrientation=1;m.alpha=1;if(m instanceof PBRMaterial){m.twoSidedLighting=false;m.transparencyMode=0;}this.surfaceMaterials.set(name,m);}
 return this.surfaceMaterials.get(name)!;
 }
 resourceStats(){
  const containers=[...this.containers.entries()].filter(([n])=>n.startsWith('elmir_house_residential')||n==='elmir_central_monument_01');
  return {sharedExpansionMaterials:this.sharedMaterials.size,sharedExpansionTextures:this.sharedTextures.size,totalContainers:this.containers.size,newContainers:containers.length,materials:containers.reduce((n,[,c])=>n+c.materials.length,0),textures:new Set(containers.flatMap(([,c])=>c.textures.map(t=>t.uniqueId))).size};
 }
 material(name:AssetName){return this.containers.get(name)!.materials[0];}
 instantiate(name:AssetName,x:number,z:number,y=0){
  const entry=this.containers.get(name)!.instantiateModelsToScene(n=>name+'_'+n,false,{doNotInstantiate:false});
  const root=new TransformNode(name+'-placement',this.scene);root.position.set(x,y,z);
  const meshes:AbstractMesh[]=[];
  for(const node of entry.rootNodes){node.parent=root;for(const m of node.getChildMeshes()){m.isPickable=false;m.receiveShadows=true;meshes.push(m);}}
  return {root,meshes,dispose:()=>root.dispose(false,false)};
 }
 place(name:AssetName,x:number,z:number,y=0):AbstractMesh[]{
  const instance=this.containers.get(name)!.instantiateModelsToScene(n=>name+'_'+n,false,{doNotInstantiate:false});
  const meshes:AbstractMesh[]=[];
  for(const root of instance.rootNodes){
   (root as TransformNode).position.addInPlace(new Vector3(x,y,z));
   for(const m of root.getChildMeshes()){m.isPickable=false;m.receiveShadows=true;meshes.push(m);}
  }
  return meshes;
 }
}
